import React, { FC, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ReactComponent as InstaIcon } from "../../assets/svg/instagram.svg";
import { ReactComponent as LinkedinIcon } from "../../assets/svg/linkedin.svg";
import { ReactComponent as FacebookIcon } from "../../assets/svg/facebook.svg";
import { IconTrash, IconUser } from "../../utils/icons";
import { cldUrl } from "../../utils/cloudinary";
import { ISinglePro } from "../../components/singlePro/state";
import SocialIcon from "../videoProduction/components/SocialIcon";
import { getTextOverflowProps } from "../pro/Listing/components/helper";
import { NoDataView } from "../../components/EmptyPanel/NoDataView";
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";
import axiosInstant from "../../api";
import { removeBrandProEndpoint } from "../../api/endpoints";
import { useUser } from "../../hooks/useUser";
import { isAM } from "../../utils";
import { formatProfessionsWithExperience } from "../../utils/professionExperience";
import { ExpertIdPill } from "../../components/ExpertIdPill/ExpertIdPill";

interface Props {
  refetch: () => void;
  experts: ISinglePro[];
}

export const BrandDetailExpertsList: FC<Props> = ({ experts, refetch }) => {
  const { id } = useParams();
  const { role } = useUser();
  const [loading, setLoading] = useState(false);
  const [deleteExpert, setDeleteExpert] = useState<ISinglePro>();

  const removeExpert = async () => {
    if (!id || !deleteExpert?.userid) return;
    setLoading(true);
    try {
      await axiosInstant.delete(
        removeBrandProEndpoint(id, String(deleteExpert.userid)),
      );
      setDeleteExpert(undefined);
      refetch();
    } catch (err) {
      toast.error("Failed to deassociate expert");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <span className="text-text-1">{experts.length} Experts</span>
        </div>
        <hr className="flex-grow border-t border-border-token" />
      </div>

      {experts.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {experts.map((expert) => (
            <div
              key={expert.userid}
              className="flex flex-col min-w-[264px] gap-3 p-4 rounded-lg border bg-surface border-border-token w-full"
            >
              <div className="flex items-start justify-between w-full gap-2 mb-1">
                {expert?.avatar ? (
                  <img
                    className="w-[80px] h-[80px] object-cover object-top rounded-[6px] shrink-0"
                    src={cldUrl(expert.avatar, { w: 160, h: 160, crop: "fill", gravity: "face" })}
                    alt={expert?.displayName || "Expert"}
                  />
                ) : (
                  <div className="w-[80px] h-[80px] flex shrink-0 items-center justify-center rounded-[6px] bg-primary bg-opacity-20">
                    <IconUser width={32} height={32} />
                  </div>
                )}
                {isAM(role) && (
                  <div
                    onClick={() => setDeleteExpert(expert)}
                    className="flex items-center justify-center cursor-pointer w-6 h-6 bg-surface hover:bg-surface-2 rounded-md text-muted"
                  >
                    <IconTrash width={16} height={16} stroke="currentColor" />
                  </div>
                )}
              </div>

              {formatProfessionsWithExperience(expert?.professions) && (
                <div className="text-[14px] h-[28px] text-text-2 font-[CircularStd] overflow-hidden whitespace-nowrap text-ellipsis max-w-full">
                  <span>{formatProfessionsWithExperience(expert?.professions)}</span>
                </div>
              )}

              <hr className="w-full border-t border-border-token" />

              <div className="flex items-center justify-between w-full">
                <Link
                  to={`/experts/${expert?.userid}/${
                    expert?.slug || "-"
                  }?proId=${expert?.userid}`}
                  className="overflow-hidden text-ellipsis whitespace-nowrap text-[18px] font-semibold leading-[132%] text-text-1 font-[CircularStd] max-w-[180px] hover:text-accent-text hover:underline cursor-pointer"
                >
                  {expert?.displayName || "Unknown Expert"}
                </Link>

                <div className="flex gap-1 items-center">
                  <ExpertIdPill userid={expert?.userid} />
                  {expert?.instagram && <SocialIcon icon={<InstaIcon />} />}
                  {expert?.linkedin && <SocialIcon icon={<LinkedinIcon />} />}
                  {expert?.facebook && <SocialIcon icon={<FacebookIcon />} />}
                </div>
              </div>

              {expert?.description?.trim() && (
                <div
                  className="text-text-2 font-[CircularStd] py-0.5"
                  style={getTextOverflowProps(4)}
                >
                  {expert?.description}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <NoDataView
          showTextInline
          Icon={IconUser}
          subText="Experts will be visible once you have projects and videos in your account."
        />
      )}

      {deleteExpert && (
        <ConfirmationDialog
          open={!!deleteExpert?.userid}
          onClose={() => setDeleteExpert(undefined)}
          onConfirm={removeExpert}
          confirmLabel="Remove"
          disabled={loading}
          title="Remove Expert"
          description={`Are you sure you want to remove the association between ${deleteExpert?.displayName} and this brand? This will not delete the expert.`}
        />
      )}
    </>
  );
};
